/**
 * @description
 * Unified API facade maintaining backward compatibility with the original monolithic api.ts.
 * This facade delegates all method calls to the appropriate service modules.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/services/api.ts.backup
 */

// Import all service instances
import { clientService } from './client/client-service';
import { clientConfigService } from './client/client-config-service';
import { deploymentService } from './deployment/deployment-service';
import { deploymentHealthService } from './deployment/deployment-health-service';
import { performanceService } from './deployment/performance-service';
import { userService } from './user-management/user-service';
import { apiKeyService } from './user-management/api-key-service';
import { vscodeLensService } from './vscode-lens/vscode-lens-service';
import { timeSeriesService } from './vscode-lens/timeseries-service';
import { reportService } from './vscode-lens/report-service';
import { anthropicService } from './anthropic/anthropic-service';
import { anthropicAssignmentService } from './anthropic/anthropic-assignment-service';
import { stripeCustomerService } from './stripe/stripe-customer-service';
import { stripeSubscriptionService } from './stripe/stripe-subscription-service';
import { stripeInvoiceService } from './stripe/stripe-invoice-service';
import { stripeProductService } from './stripe/stripe-product-service';
import { stripeUsageService } from './stripe/stripe-usage-service';
import { settingsService } from './settings/settings-service';
import { platformOAuthService } from './platform/platform-oauth-service';
import { systemService } from './system/system-service';
import { marketplaceService } from './marketplace/marketplace-service';

// Import all types
export * from './core/types';
export * from './client/types';
export * from './deployment/types';
export * from './user-management/types';
export * from './vscode-lens/types';
export * from './anthropic/types';
export * from './stripe/types';
export * from './settings/types';
export * from './platform/types';
export * from './system/types';
export * from './marketplace/types';

/**
 * Unified API client that delegates to service modules
 * Maintains exact same interface as original monolithic api.ts
 */
class APIClient {
  // Client Management Methods
  getClients = clientService.getClients.bind(clientService);
  getClient = clientService.getClient.bind(clientService);
  createClient = clientService.createClient.bind(clientService);
  updateClient = clientService.updateClient.bind(clientService);
  deleteClient = clientService.deleteClient.bind(clientService);
  provisionDatabase = clientService.provisionDatabase.bind(clientService);
  getDomainSettings = clientService.getDomainSettings.bind(clientService);
  updateDomainSettings = clientService.updateDomainSettings.bind(clientService);

  // Client Configuration Methods
  getClientConfigs = clientConfigService.getClientConfigs.bind(clientConfigService);
  getClientConfig = clientConfigService.getClientConfig.bind(clientConfigService);
  updateClientConfig = clientConfigService.updateClientConfig.bind(clientConfigService);
  deleteClientConfig = clientConfigService.deleteClientConfig.bind(clientConfigService);
  bulkUpdateClientConfigs = clientConfigService.bulkUpdateClientConfigs.bind(clientConfigService);
  initializeClientConfigs = clientConfigService.initializeClientConfigs.bind(clientConfigService);

  // Deployment Methods
  getDeployments = deploymentService.getDeployments.bind(deploymentService);
  getClientDeployments = deploymentService.getClientDeployments.bind(deploymentService);
  createDeployment = deploymentService.createDeployment.bind(deploymentService);
  getDeploymentStatus = deploymentService.getDeploymentStatus.bind(deploymentService);
  rollbackDeployment = deploymentService.rollbackDeployment.bind(deploymentService);
  deleteDeployment = deploymentService.deleteDeployment.bind(deploymentService);
  getDeploymentDetails = deploymentService.getDeploymentDetails.bind(deploymentService);
  getDeploymentReadiness = deploymentService.getDeploymentReadiness.bind(deploymentService);
  initializeClientDefaults = deploymentService.initializeClientDefaults.bind(deploymentService);
  startDeployment = deploymentService.startDeployment.bind(deploymentService);
  stopDeployment = deploymentService.stopDeployment.bind(deploymentService);
  getClientDeploymentStatus = deploymentService.getClientDeploymentStatus.bind(deploymentService);
  getClientDeploymentLogs = deploymentService.getClientDeploymentLogs.bind(deploymentService);
  deployToProduction = deploymentService.deployToProduction.bind(deploymentService);
  startLocalDeployment = deploymentService.startLocalDeployment.bind(deploymentService);
  stopLocalDeployment = deploymentService.stopLocalDeployment.bind(deploymentService);
  getProductionStatus = deploymentService.getProductionStatus.bind(deploymentService);

  // Deployment Health Methods
  getDeploymentHealth = deploymentHealthService.getDeploymentHealth.bind(deploymentHealthService);
  getDeploymentHealthHistory = deploymentHealthService.getDeploymentHealthHistory.bind(deploymentHealthService);
  scheduleDeploymentHealthChecks = deploymentHealthService.scheduleDeploymentHealthChecks.bind(deploymentHealthService);

  // Performance Methods
  getPerformanceAnalytics = performanceService.getPerformanceAnalytics.bind(performanceService);
  getTimeSeriesMetrics = performanceService.getTimeSeriesMetrics.bind(performanceService);
  storePerformanceSnapshot = performanceService.storePerformanceSnapshot.bind(performanceService);

  // User Management Methods
  getUsers = userService.getUsers.bind(userService);
  getClientUsers = userService.getClientUsers.bind(userService);
  addClientUser = userService.addClientUser.bind(userService);
  updateClientUser = userService.updateClientUser.bind(userService);
  deleteClientUser = userService.deleteClientUser.bind(userService);
  syncUsersFromPlatform = userService.syncUsersFromPlatform.bind(userService);
  updateUserRoleAndKey = userService.updateUserRoleAndKey.bind(userService);

  // API Key Methods
  getUserApiKeys = apiKeyService.getUserApiKeys.bind(apiKeyService);
  addUserApiKey = apiKeyService.addUserApiKey.bind(apiKeyService);
  deleteUserApiKey = apiKeyService.deleteUserApiKey.bind(apiKeyService);
  getAvailableApiKeys = apiKeyService.getAvailableApiKeys.bind(apiKeyService);
  getClientApiKeys = apiKeyService.getClientApiKeys.bind(apiKeyService);
  addClientApiKey = apiKeyService.addClientApiKey.bind(apiKeyService);
  deleteClientApiKey = apiKeyService.deleteClientApiKey.bind(apiKeyService);

  // VSCode Lens Methods
  getVSCodeLensTables = vscodeLensService.getVSCodeLensTables.bind(vscodeLensService);
  createVSCodeLensStream = vscodeLensService.createVSCodeLensStream.bind(vscodeLensService);

  // Time-Series Methods
  getTimeSeries = timeSeriesService.getTimeSeries.bind(timeSeriesService);
  getSnapshots = timeSeriesService.getSnapshots.bind(timeSeriesService);
  createSnapshot = timeSeriesService.createSnapshot.bind(timeSeriesService);
  getSnapshotById = timeSeriesService.getSnapshotById.bind(timeSeriesService);
  compareSnapshots = timeSeriesService.compareSnapshots.bind(timeSeriesService);
  cleanupSnapshots = timeSeriesService.cleanupSnapshots.bind(timeSeriesService);

  // Report Methods
  getReportSchedules = reportService.getReportSchedules.bind(reportService);
  getReportSchedule = reportService.getReportSchedule.bind(reportService);
  createReportSchedule = reportService.createReportSchedule.bind(reportService);
  updateReportSchedule = reportService.updateReportSchedule.bind(reportService);
  deleteReportSchedule = reportService.deleteReportSchedule.bind(reportService);
  runReportSchedule = reportService.runReportSchedule.bind(reportService);
  getGeneratedReports = reportService.getGeneratedReports.bind(reportService);
  getGeneratedReport = reportService.getGeneratedReport.bind(reportService);
  getReportDownloadUrl = reportService.getReportDownloadUrl.bind(reportService);
  deleteGeneratedReport = reportService.deleteGeneratedReport.bind(reportService);
  cleanupExpiredReports = reportService.cleanupExpiredReports.bind(reportService);

  // Platform OAuth Methods
  getPlatformOAuthConfig = platformOAuthService.getPlatformOAuthConfig.bind(platformOAuthService);
  initializePlatformOAuth = platformOAuthService.initializePlatformOAuth.bind(platformOAuthService);
  verifyPlatformConnection = platformOAuthService.verifyPlatformConnection.bind(platformOAuthService);
  refreshPlatformToken = platformOAuthService.refreshPlatformToken.bind(platformOAuthService);
  disconnectPlatform = platformOAuthService.disconnectPlatform.bind(platformOAuthService);

  // Anthropic Methods
  getAnthropicOrganization = anthropicService.getAnthropicOrganization.bind(anthropicService);
  getAnthropicWorkspaces = anthropicService.getAnthropicWorkspaces.bind(anthropicService);
  createAnthropicWorkspace = anthropicService.createAnthropicWorkspace.bind(anthropicService);
  updateAnthropicWorkspace = anthropicService.updateAnthropicWorkspace.bind(anthropicService);
  archiveAnthropicWorkspace = anthropicService.archiveAnthropicWorkspace.bind(anthropicService);
  assignClientsToWorkspace = anthropicService.assignClientsToWorkspace.bind(anthropicService);
  getAnthropicApiKeys = anthropicService.getAnthropicApiKeys.bind(anthropicService);
  getAnthropicCost = anthropicService.getAnthropicCost.bind(anthropicService);
  getAnthropicUsage = anthropicService.getAnthropicUsage.bind(anthropicService);

  // Anthropic Assignment Methods
  assignWorkspaceUsers = anthropicAssignmentService.assignWorkspaceUsers.bind(anthropicAssignmentService);
  assignUserAnthropicKey = anthropicAssignmentService.assignUserAnthropicKey.bind(anthropicAssignmentService);
  removeUserAnthropicKey = anthropicAssignmentService.removeUserAnthropicKey.bind(anthropicAssignmentService);
  getClientAnthropicAssignments = anthropicAssignmentService.getClientAnthropicAssignments.bind(anthropicAssignmentService);

  // Stripe Customer Methods
  getStripeCustomer = stripeCustomerService.getStripeCustomer.bind(stripeCustomerService);
  createStripeCustomer = stripeCustomerService.createStripeCustomer.bind(stripeCustomerService);
  linkStripeCustomer = stripeCustomerService.linkStripeCustomer.bind(stripeCustomerService);
  getCustomerPortalUrl = stripeCustomerService.getCustomerPortalUrl.bind(stripeCustomerService);
  getPaymentMethods = stripeCustomerService.getPaymentMethods.bind(stripeCustomerService);

  // Stripe Subscription Methods
  getSubscriptions = stripeSubscriptionService.getSubscriptions.bind(stripeSubscriptionService);
  createSubscription = stripeSubscriptionService.createSubscription.bind(stripeSubscriptionService);
  cancelSubscription = stripeSubscriptionService.cancelSubscription.bind(stripeSubscriptionService);
  deleteSubscription = stripeSubscriptionService.deleteSubscription.bind(stripeSubscriptionService);
  getSubscriptionPlans = stripeSubscriptionService.getSubscriptionPlans.bind(stripeSubscriptionService);
  createSubscriptionPlan = stripeSubscriptionService.createSubscriptionPlan.bind(stripeSubscriptionService);
  updateSubscription = stripeSubscriptionService.updateSubscription.bind(stripeSubscriptionService);

  // Stripe Invoice Methods
  getInvoices = stripeInvoiceService.getInvoices.bind(stripeInvoiceService);
  bulkUpdateInvoices = stripeInvoiceService.bulkUpdateInvoices.bind(stripeInvoiceService);
  createManualInvoice = stripeInvoiceService.createManualInvoice.bind(stripeInvoiceService);
  createSubscriptionInvoice = stripeInvoiceService.createSubscriptionInvoice.bind(stripeInvoiceService);
  generateUsageInvoice = stripeInvoiceService.generateUsageInvoice.bind(stripeInvoiceService);

  // Stripe Product Methods
  getProducts = stripeProductService.getProducts.bind(stripeProductService);
  createProduct = stripeProductService.createProduct.bind(stripeProductService);
  getAllPrices = stripeProductService.getAllPrices.bind(stripeProductService);
  syncStripeProducts = stripeProductService.syncStripeProducts.bind(stripeProductService);
  testStripeConnection = stripeProductService.testStripeConnection.bind(stripeProductService);

  // Stripe Usage Methods
  getPricingConfig = stripeUsageService.getPricingConfig.bind(stripeUsageService);
  updatePricingConfig = stripeUsageService.updatePricingConfig.bind(stripeUsageService);
  getUsageAccumulator = stripeUsageService.getUsageAccumulator.bind(stripeUsageService);
  getUsageData = stripeUsageService.getUsageData.bind(stripeUsageService);
  syncAnthropicCosts = stripeUsageService.syncAnthropicCosts.bind(stripeUsageService);
  checkThresholds = stripeUsageService.checkThresholds.bind(stripeUsageService);
  getBillingStats = stripeUsageService.getBillingStats.bind(stripeUsageService);

  // Settings Methods
  getSettings = settingsService.getSettings.bind(settingsService);
  updateSetting = settingsService.updateSetting.bind(settingsService);
  updateSettings = settingsService.updateSettings.bind(settingsService);

  // System Methods
  getMetrics = systemService.getMetrics.bind(systemService);
  getHealth = systemService.getHealth.bind(systemService);

  // Marketplace Methods
  getMarketplaceConfig = marketplaceService.getMarketplaceConfig.bind(marketplaceService);
  updateMarketplaceConfig = marketplaceService.updateMarketplaceConfig.bind(marketplaceService);
  testMarketplaceDeployment = marketplaceService.testMarketplaceDeployment.bind(marketplaceService);
}

/**
 * Singleton API client instance
 * Maintains backward compatibility with original api.ts
 */
export const api = new APIClient();

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */